import { compact } from 'lodash'
import { FILTER_SEPARATOR, decodeFilters } from 'app/filter.utils'
import { logWarn } from 'lib/logging'

/**
 * Per-device "sticky" location: the location a user picked once, carried onto
 * every page that can filter by location until they explicitly choose All.
 *
 * Two URL mechanisms exist and this module does not unify them:
 *   `locationIds`   comma list, read by useLocationFilter (pipelines board, appointments calendar).
 *   `filters`       a `locationId in (...)` clause inside the table filter
 *                   string, read by readLocationIds and the filter registries.
 * `filtersDefault` is the `filters` mechanism on a page whose tab carries
 * predefined filters. Writing a `filters` param there replaces the tab's
 * defaults wholesale, so the page applies the remembered ids as one more tab
 * default itself (see useStickyLocationDefault) and this module only observes.
 */
type LocationMechanism = `locationIds` | `filters` | `filtersDefault`

function locationMechanismFor(pathname: string): LocationMechanism | null {
  if (pathname === `/pipelines` || pathname === `/appointments-calendar`) return `locationIds`
  if (pathname === `/action-center` || pathname.startsWith(`/action-center/`) || pathname === `/treatment-plans`)
    return `filters`
  if (pathname === `/contacts` || pathname === `/activities` || pathname === `/tasks`) return `filtersDefault`
  return null
}

const STORAGE_PREFIX = `trk:stickyLocation:`

// Org-level users have no workspace; their remembered ids still need a slot
// that no workspace can read.
function storageKey(workspaceId: string | null | undefined): string {
  return `${STORAGE_PREFIX}${workspaceId ?? `org`}`
}

export function readStickyLocation(workspaceId: string | null | undefined): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(storageKey(workspaceId)) ?? `[]`)
    return Array.isArray(parsed) ? compact(parsed.filter((id) => typeof id === `string`)) : []
  } catch (err) {
    logWarn(`stickyLocation`, `ignoring unreadable remembered location`, err)
    return []
  }
}

/** An empty selection is "All locations": nothing to remember, so the entry goes away. */
export function rememberStickyLocation(workspaceId: string | null | undefined, ids: string[]): void {
  if (ids.length) {
    localStorage.setItem(storageKey(workspaceId), JSON.stringify(ids))
  } else {
    localStorage.removeItem(storageKey(workspaceId))
  }
}

const LOCATION_ATTRIBUTE = `locationId`

/**
 * Appends the location clause to an encoded `filters` string.
 *
 * Works on the raw segments rather than decode + encode: decodeFilters expands
 * date presets such as `last_30_days` into concrete dates, and re-encoding
 * would quietly rewrite a filter the user never touched.
 */
function mergeLocationClause(filters: string | null | undefined, ids: string[]): string {
  const others = (filters ?? ``)
    .split(FILTER_SEPARATOR)
    .filter((segment) => segment && !segment.startsWith(`${LOCATION_ATTRIBUTE} `))
  const clause = `${LOCATION_ATTRIBUTE} in (${ids.map((id) => `'${id}'`).join(`,`)})`
  return [...others, clause].join(FILTER_SEPARATOR)
}

export type UrlSnapshot = { pathname: string; search: string }

export type StickyDecision =
  | { action: `none` }
  | { action: `remember`; ids: string[] }
  | { action: `forget` }
  | { action: `inject`; params: Record<string, string> }

type UrlLocation =
  | { kind: `selected`; ids: string[] }
  /** The user chose All locations: the `attribute_exists` clause the square button writes. */
  | { kind: `all` }
  /** A `filters` param is present but carries no location at all. */
  | { kind: `cleared` }
  | { kind: `none` }

function readUrlLocation(mechanism: LocationMechanism, params: URLSearchParams): UrlLocation {
  if (mechanism === `locationIds`) {
    const ids = compact(params.get(`locationIds`)?.split(`,`) ?? [])
    return ids.length ? { kind: `selected`, ids } : { kind: `none` }
  }
  if (!params.has(`filters`)) return { kind: `none` }
  const clause = decodeFilters(params.get(`filters`)).find((filter) => filter.attribute === LOCATION_ATTRIBUTE)
  if (!clause) return { kind: `cleared` }
  if (clause.operator !== `in`) return { kind: `all` }
  const ids = compact((Array.isArray(clause.value) ? clause.value : [clause.value]) as string[])
  return ids.length ? { kind: `selected`, ids } : { kind: `all` }
}

/**
 * What the shell should do after a URL change. Pure so the transition rules
 * are testable without a router:
 *   selection in the URL            remember it (a shared link with another location wins)
 *   All locations, or the location
 *   dropped on the same page + tab  forget it
 *   page or tab changed, nothing in
 *   the URL, something remembered   inject it (only where a URL write is safe)
 */
export function decideStickyLocation({
  previous,
  current,
  remembered,
}: {
  previous: UrlSnapshot | null
  current: UrlSnapshot
  remembered: string[]
}): StickyDecision {
  const mechanism = locationMechanismFor(current.pathname)
  if (!mechanism) return { action: `none` }

  const params = new URLSearchParams(current.search)
  const location = readUrlLocation(mechanism, params)
  if (location.kind === `selected`) return { action: `remember`, ids: location.ids }
  if (location.kind === `all`) return { action: `forget` }

  const previousParams = previous ? new URLSearchParams(previous.search) : null
  const samePage =
    previous != null &&
    previousParams != null &&
    previous.pathname === current.pathname &&
    previousParams.get(`tab`) === params.get(`tab`)
  if (samePage) {
    if (location.kind === `cleared`) return { action: `forget` }
    const previousMechanism = locationMechanismFor(previous.pathname)
    const previousLocation = previousMechanism ? readUrlLocation(previousMechanism, previousParams) : null
    return previousLocation?.kind === `selected` ? { action: `forget` } : { action: `none` }
  }

  if (!remembered.length || mechanism === `filtersDefault`) return { action: `none` }
  if (mechanism === `locationIds`) return { action: `inject`, params: { locationIds: remembered.join(`,`) } }
  return { action: `inject`, params: { filters: mergeLocationClause(params.get(`filters`), remembered) } }
}
